/**
 * localStorage migration shim — Session I-79 (Feb 2026).
 *
 * Iter 68 hot-fix (Feb 2026): the previous global rebrand pass mechanically
 * rewrote every occurrence of the old prefix to `smin_` in this file too,
 * turning every entry in RENAMES into a same-key → same-key mapping. The
 * `localStorage.removeItem(oldK)` call on line 41 then executed
 * unconditionally on live session keys (`smin_token`, `smin_user`), silently
 * logging the user out on every full page reload. Fix: skip the removal
 * whenever `oldK === newK`, which turns this whole module into a safe
 * no-op until we ever need to rename storage keys again.
 */
const RENAMES = [
  // Auth surface. Historically these lived under an older prefix; keep the
  // slots here as a template for the next rename cycle. Right now old===new,
  // so the loop below is a no-op for these entries.
  ["smin_token",           "smin_token"],
  ["smin_user",            "smin_user"],
  ["smin_pre_token",       "smin_pre_token"],
  ["smin_pending_stage",   "smin_pending_stage"],
  ["smin_currencies_v1",   "smin_currencies_v1"],
  ["smin_ws_profiles",     "smin_ws_profiles"],
  ["smin_ws_active",       "smin_ws_active"],
  ["smin_master_show_advanced", "smin_master_show_advanced"],
];

// Real legacy prefixes we still want to sweep off older devices. These MUST
// differ from PREFIX_NEW — otherwise we'd wipe live keys.
const LEGACY_PREFIXES = ["smino_", "geomesh_"];
const PREFIX_NEW = "smin_";

export function migrateLegacyStorage() {
  if (typeof window === "undefined" || !window.localStorage) return 0;
  let migrated = 0;

  // 1. Explicit renames — same-key entries are safe no-ops.
  RENAMES.forEach(([oldK, newK]) => {
    if (oldK === newK) return; // 🛡 never touch live keys
    try {
      const v = localStorage.getItem(oldK);
      if (v === null) return;
      if (localStorage.getItem(newK) === null) {
        localStorage.setItem(newK, v);
      }
      localStorage.removeItem(oldK);
      migrated++;
    } catch (e) { /* quota / privacy mode — ignore */ }
  });

  // 2. Sweep every remaining truly-legacy key (`smino_*`, `geomesh_*`, …)
  //    and migrate it to the current `smin_*` prefix.
  try {
    const legacy = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && LEGACY_PREFIXES.some((p) => k.startsWith(p))) legacy.push(k);
    }
    legacy.forEach((k) => {
      const oldPrefix = LEGACY_PREFIXES.find((p) => k.startsWith(p));
      const newK = PREFIX_NEW + k.slice(oldPrefix.length);
      if (newK === k) return; // paranoid safeguard
      const v = localStorage.getItem(k);
      if (v !== null && localStorage.getItem(newK) === null) {
        localStorage.setItem(newK, v);
      }
      localStorage.removeItem(k);
      migrated++;
    });
  } catch (e) { /* ignore */ }

  if (migrated > 0) {
    console.info(`[SMIN] Migrated ${migrated} legacy localStorage key(s)`);
  }
  return migrated;
}
